import {
  selectRegistrationRevision,
  selectRevisionEntities,
} from "@/data-preparation-tool/store/revision/revision-entity-selectors";
import { hasDraftChanges } from "@/utils/capture-tree-utils";
import {
  RegistrationRevision,
  RegistrationState,
  RevisionType,
} from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "./store";

// Note that the revisions are already sorted by creation date (newest first) in the store.

/**
 * @param state The current state of the store
 * @returns Array of all revisions of the project, sorted by creation date (newest first)
 */
export function selectRevisions(state: RootState): RegistrationRevision[] {
  return state.revisions.revisions;
}

/**
 * Determine if the open draft revision has any changes.
 * ASSUMPTION: The latest draft revision is the one loaded in the store.
 *
 * @param state The current state of the store
 * @returns Whether the open draft revision has changes, or false if there is no open draft revision
 */
export const selectHasChangesInDraft = createSelector(
  [selectRegistrationRevision, selectRevisionEntities],
  (revision, entities): boolean => hasDraftChanges(revision, entities),
);

/**
 * @param state The current state of the store
 * @returns The newest merged revision of the project, or undefined if there is none
 */
export const selectNewestMergedRevision: (
  state: RootState,
) => RegistrationRevision | undefined = createSelector(
  (state: RootState) => state.revisions.revisions,
  (revisions: RegistrationRevision[]) =>
    revisions.find(
      (revision) =>
        revision.state === RegistrationState.merged &&
        revision.revisionType === RevisionType.draft,
    ),
);
